import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { AudioFile, Folder } from '../types';

/**
 * Custom hook that centralizes navigation logic with state updates.
 * Ensures URL always reflects the current selection state.
 */
export function useAppNavigation() {
  const { dispatch } = useApp();
  const navigate = useNavigate();

  const selectAudioFileWithoutNav = useCallback(
    (file: AudioFile | null) => {
      dispatch({ type: 'SET_SELECTED_AUDIO_FILE', payload: file });
    },
    [dispatch]
  );

  const selectAudioFile = useCallback(
    (file: AudioFile | null) => {
      selectAudioFileWithoutNav(file);

      // Update URL to reflect the selection
      if (file) {
        navigate(`/dashboard/file/${file.id}`);
      } else {
        navigate('/dashboard');
      }
    },
    [selectAudioFileWithoutNav, navigate]
  );

  const selectFolderWithoutNav = useCallback(
    (folder: Folder | null) => {
      dispatch({ type: 'SET_SELECTED_FOLDER', payload: folder });
    },
    [dispatch]
  );

  const selectFolder = useCallback(
    (folder: Folder | null) => {
      selectFolderWithoutNav(folder);

      // Update URL to reflect the selection
      if (folder) {
        if (folder.id === 'common') {
          navigate('/dashboard/folder/common');
        } else {
          navigate(`/dashboard/folder/${folder.id}`);
        }
      } else {
        navigate('/dashboard');
      }
    },
    [selectFolderWithoutNav, navigate]
  );

  const clearSelectionWithoutNav = useCallback(() => {
    selectAudioFileWithoutNav(null);
    selectFolderWithoutNav(null);
  }, [selectAudioFileWithoutNav, selectFolderWithoutNav]);

  const clearSelection = useCallback(() => {
    clearSelectionWithoutNav();
    navigate('/dashboard');
  }, [clearSelectionWithoutNav, navigate]);

  return {
    selectAudioFile,
    selectFolder,
    clearSelection,
    selectAudioFileWithoutNav,
    selectFolderWithoutNav,
    clearSelectionWithoutNav,
  };
}
